import { useMutation } from "@apollo/client";
import type {
  CreateConnectionInput,
  CreateConnectionPayload,
  ConnectionInput,
  Connection,
} from "@casey/db-types";
import {
  CREATE_INTERVIEW_INVITE,
  GET_ORGANIZATION_INTERVIEW_INVITES,
  GET_CONNECTIONS_BY_AGENT,
} from "../gql";

interface CreateConnectionVariables {
  input: CreateConnectionInput;
}

interface CreateConnectionResponse {
  createConnection: CreateConnectionPayload;
}

export interface UseCreateConnectionResult {
  createConnection: (inviteData: ConnectionInput) => Promise<Connection | null>;
  loading: boolean;
  error: Error | null;
}

export function useCreateConnection(): UseCreateConnectionResult {
  const [createInviteMutation, { loading, error }] = useMutation<
    CreateConnectionResponse,
    CreateConnectionVariables
  >(CREATE_INTERVIEW_INVITE, {
    errorPolicy: "all",
    onError: (error) => {
      console.error("Interview invite creation error:", error);
    },
    // Refetch invites after creation
    refetchQueries: [GET_ORGANIZATION_INTERVIEW_INVITES],
    // Also update connections by agent cache
    update: (cache, { data }) => {
      const createdConnection = data?.createConnection?.connection;
      if (createdConnection?.agentId) {
        // Evict the connections by agent query to force refetch
        cache.evict({
          id: cache.identify({
            __typename: "Agent",
            id: createdConnection.agentId,
          }),
          fieldName: "connections",
        });
        cache.gc();
      }
    },
  });

  const createConnection = async (
    inviteData: ConnectionInput,
  ): Promise<Connection | null> => {
    try {
      const result = await createInviteMutation({
        variables: {
          input: {
            connection: inviteData,
          },
        },
      });

      const createdInvite = result.data?.createConnection?.connection;
      return createdInvite || null;
    } catch (err) {
      console.error("Error creating interview invite:", err);
      throw err;
    }
  };

  return {
    createConnection,
    loading,
    error: error || null,
  };
}
